import type { ColDef } from 'ag-grid-community';

import { isServiceColumn } from '@/components/AgGrid/helpers';

// Type-level flex weight applied to every column via `useAgGridColumnTypes` (FLEX_DEFAULT). Kept
// here so the flex design is resolvable without importing the hook module — `getDesignedFlex` below
// and `useAgGridColumnTypes` share this single source of truth.
export const DEFAULT_COLUMN_FLEX = 1;

// Field-level overrides for the handful of bank-feed columns that have EXPLICIT product
// feedback (DA-19096). Everything else is sized generically by column `type` in
// useAgGridColumnTypes — so a backend that delivers a different column set (the credit-card
// grid) is sized correctly with no change here. Keyed by BE `field`; merged OVER the
// type-level config in the useColumnDefs pipeline (direct colDef props win in AG Grid).
//
//   - account: narrower floor (unified provider+number column, DA-19447; #1 "Bank can be shorter").
//   - description (Details) / debtor_name (Additional info): wide `minWidth` floor so they
//     stay wide on a narrow (13") screen — the secondary columns scroll instead — plus a
//     higher flex weight, so on a wide (27") screen they take the largest share of the free
//     space and grow well beyond the floor (#2 "as wide as possible").
//   - debit / credit: fixed 150px width. Their default right-pin is NOT a colDef prop — it
//     lives in DEFAULT_BANK_FEED_GRID_STATE (useBankFeedTransactionsGridState) as grid state,
//     so the user can re-pin / unpin and that choice persists (#3); RTL side-flip + the
//     persisted override are handled there. While pinned (the default) AG Grid ignores the
//     type-level flex, so the 150px applies.
type ColumnOverride = {
  flex?: number;
  minWidth?: number;
  width?: number;
};

export const BANK_FEED_COLUMN_OVERRIDES: Record<string, ColumnOverride> = {
  account: { minWidth: 160 },
  description: { flex: 2, minWidth: 240 },
  debtor_name: { flex: 2, minWidth: 240 },
  debit: { width: 150 },
  credit: { width: 150 },
};

// Adapter step for the useColumnDefs pipeline. Runs before the service column is appended,
// so it only sees BE data columns: each feedback-targeted column gets its override merged
// over the type-level config; every other column is left to its type defaults.
export const applyColumnOverrides =
  (overrides: Record<string, ColumnOverride>) =>
  (columns: ColDef[]): ColDef[] =>
    columns.map((col) => {
      const override = col.field != null ? overrides[col.field] : undefined;
      return override != null ? { ...col, ...override } : col;
    });

// The designed flex weight for a column, or `undefined` for columns that must stay fixed-width.
// Used to re-apply flex after AG Grid nulls it on a manual resize (`Column.setActualWidth`), which
// otherwise leaves empty space when a column is later hidden (DA-19187). Every data column flexes
// with the type-level default weight EXCEPT the wide feedback columns (`flex: 2`) and the
// fixed-width amount columns (`debit` / `credit` — `width` override, never flex). The service
// column never flexes. This is the source of truth, NOT `column.getColDef().flex` — the merged
// colDef carries a stray type-level `flex: 1` on the fixed amount columns.
export const getDesignedFlex = (colId: string): number | undefined => {
  if (isServiceColumn(colId)) return undefined;
  const override = BANK_FEED_COLUMN_OVERRIDES[colId];
  if (override?.width != null) return undefined;
  return override?.flex ?? DEFAULT_COLUMN_FLEX;
};
